import { APP_ROUTES, UserRole } from '@constants';
import { request } from '@common/utils/request';
import { getAppConfigurations, getTwoISOLanguage } from '@/common/utils';
import { AppointmentAvailabilityDto } from '../api/wisoryApiClient';

const { cognitoAuthDomain, cognitoClientId, domainUrl } = getAppConfigurations();
const authorizeUrl = `${domainUrl}${APP_ROUTES.AUTHORIZE}`;

export function getCognitoLoginUrl(appRedirectUrl: string) {
  const baseUrl = `https://${cognitoAuthDomain}/login`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoClientId,
    redirect_uri: authorizeUrl,
    state: appRedirectUrl,
  });

  return `${baseUrl}?${params.toString()}`;
}

export function refreshAccessToken(refreshToken: string) {
  return request.post<API.UserSessionResult>(
    '/login/refresh-token',
    { refreshToken },
    { skipGlobalErrorMessage: true }
  );
}

export function getUserInfo() {
  return request.get<API.UserInfoModel>('/api/users/me', { skipInterceptor: true });
}

export function updatePassword(data: API.UserPasswordChangeParams) {
  return request.put<API.UserInfoModel>(`/api/users/me/password`, data, {
    skipGlobalErrorMessage: true,
  });
}

export function getUserSessions(params?: {
  periodStartDate?: string;
  periodEndDate?: string;
  includedCancelledEvent?: boolean;
}) {
  return request.get<API.CalendarEventModel[]>(`/calendarEvents`, { params });
}

export function sendOTP(emailAddress: string) {
  return request.post<void>(`/login/sendOtp`, { emailAddress });
}

export function sendCognitoOTP(emailAddress: string) {
  return request.post<API.OtpChallengeView>(`/login/otp-login`, { emailAddress });
}
export function verifyOTP(emailAddress: string, token: string) {
  return request.post<API.UserSessionResult>(
    `/login`,
    { emailAddress, token },
    { skipGlobalErrorMessage: true }
  );
}

export function getCompanyDetails(companyId: number) {
  return request.get<API.CompanyModel>(`/api/companies/${companyId}`);
}

export function getCompanyCoinHistories(companyId: number) {
  return request.get<API.PaginatedResult<API.SessionHistoryModel>>(
    `api/users/me/companies/${companyId}/coin-histories?pageSize=999&currentPage=0&dynamicFilters=`
  );
}

export function getCompanyUsers(companyId: number) {
  return request.get<API.PaginatedResult<API.ProfileCompanyUserModel>>(
    `/api/users/me/companies/${companyId}/users?pageSize=20&currentPage=1&dynamicFilters=`
  );
}

export function updateCompany(companyId: number, params: API.UpdateCompanyParams) {
  return request.put<void>(`/api/companies/${companyId}`, params);
}

export function createAddress(params: API.AddressModel) {
  return request.post<API.AddressModel>(`/api/addresses`, params);
}

export function updateAddress(params: API.AddressModel) {
  return request.put<{ id: number }>(`/api/addresses/${params.id}`, params);
}

export function linkAddressToCompany(
  companyId: number,
  addressId: number,
  params: {
    isPrimaryAddress: boolean;
  }
) {
  return request.post<API.CompanyModel>(
    `/api/companies/${companyId}/addresses/${addressId}`,
    params
  );
}

export function sendOTPToVerifyNewPhoneNumber(userId: number, phoneNumber: string) {
  return request.post<void>(`/api/users/me/phone-number-otp`, { phoneNumber });
}

export function verifyOTPForNewPhoneNumber(userId: number, otp: string, phoneNumber: string) {
  return request.put<void>(`/api/users/me/phone-number`, {
    otp,
    phoneNumber,
  });
}

export function sendOTPToVerifyNewEmailAddress(userId: number, emailAddress: string) {
  return request.post<void>(`/api/users/me/email-otp`, { emailAddress });
}

export function verifyOTPForNewEmailAddress(userId: number, otp: string, emailAddress: string) {
  return request.put<void>(`/api/users/me/email`, { emailAddress, otp });
}

export function updateProfile(userId: number, data: API.PatchOperation[]) {
  return request.patch<API.UserInfoModel>(`/api/users/me`, data);
}

export function createAnonymousUser(data: API.CreateAnonymousUserView) {
  return request.post<API.AnonymousUserResponse>(`/api/v1/anonymous-users`, data, {
    apiGroup: 'profile',
    skipGlobalErrorMessage: true,
  });
}

export function setUserSkills(skillIds: string[]) {
  return request.put<void>(
    `/api/v1/users/me/skills`,
    { skillIds },
    {
      apiGroup: 'profile',
    }
  );
}

export function getAdvisorInterestRequests(companyId: number) {
  return request.get<API.PaginatedResult<API.UserInfoModel>>(
    `/api/companies/${companyId}/users?pageSize=20&currentPage=1&dynamicFilters=`
  );
}

export function getAdvisorAvailablities(
  advisorId: string,
  params: {
    periodFromDate?: string;
    periodToDate?: string;
  }
) {
  return request.get<API.AppointmentAvailabilityDto[]>(`/calendarAvailability/${advisorId}`, {
    params,
  });
}

export function getAdvisorInfo(advisorId: string) {
  return request.get<API.UserInfoModel>(`/api/users/${advisorId}`);
}

export function bookSlot(params: {
  appointmentAvailabilityId: number;
  platformType: string;
  shortDescription: string;
  reason: string;
  reference: string;
}) {
  return request.post<void>('/calendarEvents', params);
}

export function getOverlappingSlots(data: {
  startTime: string;
  endTime: string;
  excludeBookingId: number;
}) {
  return request.post<AppointmentAvailabilityDto[]>(
    `/appointmentAvailabilities/get-advisor-overlapping-slots`,
    data
  );
}

export function getApplicationSettings() {
  return request.get<API.PublicApplicationSettingsDto>(`/serverInfo/application-settings/public`);
}

export function getSurveyQuestionForSession(bookingId: number, role: UserRole) {
  return request.get<API.SurveyDto>(
    `/calendarEvents/${bookingId}/survey?locale=${getTwoISOLanguage()}&selectedRole=${role}`
  );
}

export function submitSurveyForSession(bookingId: string, params: API.SurveyParams) {
  return request.post<API.SurveyDto>(`/calendarEvents/${bookingId}/feedback`, params);
}

export function getSurveyContentForSession(bookingId: number) {
  return request.get<API.SurveyFeedbacksDto>(
    `/calendarEvents/${bookingId}/feedback?locale=${getTwoISOLanguage()}`
  );
}

export function getWaitingFeedbackSessions(
  params: {
    pageSize: number;
    periodStartDate?: string;
    statuses: API.SurveyFeedbackStatus[];
    readStatuses: API.SurveyReadStatus[];
  },
  isAdvisor: boolean
) {
  const query = new URLSearchParams();
  query.append('pageSize', params.pageSize.toString());
  if (params.periodStartDate) {
    query.append('periodStartDate', params.periodStartDate);
  }
  params.statuses.forEach((s) => {
    query.append('statuses', s);
  });
  params.readStatuses.forEach((s) => {
    query.append('readStatuses', s);
  });

  const apiStr = isAdvisor
    ? '/api/users/me/advisor-feedback-sessions'
    : '/api/users/me/user-session-feedbacks';

  return request.get<API.WaitingFeedbackSessionsDto>(apiStr + '?' + query.toString());
}

export function markFeedbackAsRead(bookingId: string) {
  return request.post<API.RebuySessionResult>(`/calendarEvents/${bookingId}/feedback-mark-as-read`);
}

export function rebuySession(bookingId: number, params: API.RebuySessionParams) {
  return request.post<API.RebuySessionResult>(`/calendarEvents/${bookingId}/rebuy`, params);
}

export function rebuyCheckAvalableSlot(params: API.CheckAvailableSlotParams) {
  return request.post<API.CheckAvailableSlotResult>(
    `/calendarEvents/${params.previousBookingId}/rebuy/verify`,
    params
  );
}

export function getRebookLogs(bookingId: string) {
  return request.get<API.RebookLogsResult>(`/calendarEvents/${bookingId}/rebooked-logs`);
}

export function getCompanyDescription(companyId: number) {
  return request.get<API.CompanyDescriptionView>(`/api/companies/${companyId}/description`, {
    skipGlobalErrorMessage: true,
  });
}
